// useSymbolPlacementState.js — pending-SIDC / tactical-draw state machine (ticket 08
// extraction). Owns pendingSidc/pendingTacticalSidc/pendingPointRange/pendingDrawMode/
// pendingDrawRule/pendingPlacedCount state, the tactical-SIDC resolution via MSLookup
// (handleSelectTacticalSidc), the escape-to-reset handler, the map-click-placement effect, the
// crosshair-cursor effect, and the two draw-mode-end/reset effects. Kept as one hook per the
// ticket: this is the most tangled concern in the file (mode state machine + 4 effects +
// tactical-SIDC resolution) and splitting it further mid-extraction would leave its pieces
// scattered across hooks.

import { useCallback, useEffect, useRef, useState } from 'react';
import { toLonLat } from 'ol/proj';
import { MSLookup } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import { resolveDrawMode, isParametricPointDrawRule } from '../resolveDrawMode';

export default function useSymbolPlacementState({
    mapRef, mapContainerRef, mapReady, interactionRef, activeMode, setMode, pendingTacticalSidcRef,
}) {
    const [pendingSidc, setPendingSidc] = useState(null);
    const [pendingTacticalSidc, setPendingTacticalSidc] = useState(null);
    // useMapEditor.setMode reads this ref synchronously when constructing custom draw
    // factories (LINE1 DIRATK reverse + live preview). Mirroring from React state alone is
    // too late for same-tick setMode — see syncPendingTacticalSidc in handleSelectTacticalSidc.
    // Keep this assignment as a backstop for other state updates.
    pendingTacticalSidcRef.current = pendingTacticalSidc;
    // min/max points for the currently-pending tactical SIDC (from MSInfo)
    const [pendingPointRange, setPendingPointRange] = useState(null); // {min, max} | null
    const [pendingDrawMode, setPendingDrawMode] = useState(null); // resolved mode string | null
    const [pendingDrawRule, setPendingDrawRule] = useState(null); // DrawRule number | null
    const [pendingPlacedCount, setPendingPlacedCount] = useState(0); // points placed so far

    // advanced-event-handler-refs: stable callback ref so useEditorKeyboardShortcuts
    // doesn't see a new object on every render.
    // useMapEditor.setMode reads this ref synchronously when constructing custom draw
    // factories (LINE1 DIRATK reverse + live preview). React state alone is too late —
    // setPendingTacticalSidc does not update the ref until the next render, so callers
    // that setMode in the same tick must write the ref here first.
    const syncPendingTacticalSidc = (sidc) => {
        pendingTacticalSidcRef.current = sidc;
        setPendingTacticalSidc(sidc);
    };

    const handleEscape = useCallback(() => {
        setPendingSidc(null);
        syncPendingTacticalSidc(null);
        setPendingPointRange(null);
        setPendingDrawMode(null);
        setPendingDrawRule(null);
        setPendingPlacedCount(0);
    }, []);

    // Clears just the pending-symbol-selection slice (used by MapEditorModalInner's
    // handleModeChange when the toolbar mode changes) without touching draw-mode/placed-count —
    // those are reset by the draw-mode-end effect below once `activeMode` actually clears.
    const clearPendingSymbolSelection = useCallback(() => {
        setPendingSidc(null);
        syncPendingTacticalSidc(null);
        setPendingPointRange(null);
    }, []);

    const handleSelectTacticalSidc = useCallback((sidc) => {
        syncPendingTacticalSidc(null); setPendingSidc(null);
        if (!sidc) { setMode(null); setPendingPointRange(null); setPendingDrawMode(null); return; }
        try {
            const lookup = MSLookup.getInstance();
            if (lookup.isReady()) {
                const info = lookup.getMSLInfo(sidc);
                const { mode, minPoints, maxPoints, drawRule } = resolveDrawMode(info);
                if (!mode) { setMode(null); setPendingPointRange(null); setPendingDrawMode(null); return; }
                // Parametric single-point graphics (ELLIPSE, RECTANGULAR2/3, ARC, POINT17/18) must go
                // through the tactical path (pendingTacticalSidc) so they are stored as 'tactical-point'
                // and rendered via WebRenderer with their AM/AN modifiers.
                // Static single-point symbols (POINT1–POINT16 etc.) keep the unit-icon pendingSidc path.
                if (mode === 'draw-point' && !isParametricPointDrawRule(drawRule)) {
                    setPendingSidc(sidc);
                    setMode(null);
                    return;
                }
                // Sync ref BEFORE setMode — custom factories need sidc in this same tick.
                syncPendingTacticalSidc(sidc);
                setPendingPointRange({ min: minPoints, max: maxPoints });
                setPendingDrawMode(mode);
                setPendingDrawRule(drawRule);
                setPendingPlacedCount(0);
                setMode(mode, {
                    minPoints, maxPoints, drawRule, sidc,
                    onPlacedCountChange: setPendingPlacedCount,
                });
                return;
            }
        } catch (_) { }
        syncPendingTacticalSidc(sidc);
        setPendingPointRange(null);
        setPendingDrawMode(null);
        setMode('draw-line', { sidc });
    }, [setMode]);

    // -- Map click: symbol placement -----------------------------------------
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !mapReady) return;
        const handler = (e) => {
            if (!pendingSidc) return;
            const [lng, lat] = toLonLat(e.coordinate);
            interactionRef.current?._emit('finish', { geomType: 'Point', geometry: { type: 'Point', coordinates: [lng, lat] }, sidc: pendingSidc });
        };
        map.on('click', handler);
        return () => map.un('click', handler);
    }, [mapRef, mapReady, pendingSidc, interactionRef]);

    // -- Show crosshair cursor during any pending symbol placement ---------------
    const ghostSidc = pendingSidc || (pendingDrawMode === 'draw-point' ? pendingTacticalSidc : null);
    useEffect(() => {
        const container = mapContainerRef.current;
        if (!container) return;
        container.style.cursor = ghostSidc ? 'crosshair' : '';
    }, [mapContainerRef, ghostSidc]);

    const wasInDrawModeRef = useRef(false);

    useEffect(() => {
        // Clear pending tactical state when draw mode ends — either user switches to select
        // or the interaction auto-completes (activeMode → null).
        if (activeMode) {
            wasInDrawModeRef.current = true;
        } else if (wasInDrawModeRef.current) {
            wasInDrawModeRef.current = false;
            pendingTacticalSidcRef.current = null;
            setPendingTacticalSidc(null);
            setPendingPointRange(null);
            setPendingDrawMode(null);
            setPendingDrawRule(null);
            setPendingPlacedCount(0);
        }
    }, [activeMode, pendingTacticalSidcRef]);

    // ponytail (ticket 04): pendingPlacedCount is now updated event-drivenly, via the
    // onPlacedCountChange callback passed into setMode's opts above (see handleSelectTacticalSidc)
    // — the bespoke draw interaction (customInteractions/*) invokes it on every vertex placement.
    // Still reset to 0 whenever there's no active tactical draw mode (e.g. right after mode
    // clears), matching the effect above.
    useEffect(() => {
        if (!pendingDrawMode || !pendingDrawRule) setPendingPlacedCount(0);
    }, [pendingDrawMode, pendingDrawRule]);

    return {
        pendingSidc, setPendingSidc,
        pendingTacticalSidc,
        pendingPointRange, pendingDrawMode, pendingDrawRule, pendingPlacedCount,
        handleEscape, handleSelectTacticalSidc, clearPendingSymbolSelection,
    };
}
